/**
 * PayU WebView Payment Service for Capacitor Apps
 * Handles in-app payment flow without opening external browser
 * 
 * Features:
 * - In-app WebView payment
 * - Automatic callback detection
 * - Credit refresh after payment
 * - Transaction tracking
 */

import { Capacitor } from '@capacitor/core';
import { api } from '../api';

/**
 * Initiate PayU payment in WebView (stays in app)
 */
export async function initiateWebViewPayment(paymentData) {
  console.log('🚀 Starting WebView payment flow...', paymentData);

  try {
    // 1. Call backend to get payment hash using configured api instance
    console.log('💳 Calling backend payment init using configured api');
    console.log('💳 Payment request payload:', paymentData);

    const response = await api.post('/astro/payment', paymentData);
    console.log('💳 Backend init response status:', response.status, response.statusText || '');
    const data = response.data;
    console.log('💳 Backend init response body:', data);

    if (!data || !data.success) {
      throw new Error((data && data.error) || 'Payment initialization failed');
    }

    // 2. Detect platform
    const platform = Capacitor.getPlatform();
    const isNative = Capacitor.isNativePlatform();

    console.log(`📱 Platform: ${platform}, Native: ${isNative}`);

    // 3.  Store transaction data SYNCHRONOUSLY
    const transactionData = {
      txnid: data.txnid,
      amount: data.amount,
      packageId: paymentData.packageId || null,
      purchaseId: paymentData.purchaseId || null,
      credits: paymentData.credits || 0,
      timestamp: new Date().toISOString(),
    };
    
    // Force synchronous write
    localStorage.setItem('payuTransaction', JSON.stringify(transactionData));
    
    // Small delay to ensure localStorage write completes
    await new Promise(resolve => setTimeout(resolve, 100));

    // Now submit form
    submitPaymentForm(data, paymentData);

    return { success: true };
  } catch (error) {
    console.error('❌ Payment initiation error:', error);
    throw error;
  }
}

/**
 * Create hidden form and submit to PayU
 * This keeps the payment in the app's WebView
 */
function submitPaymentForm(paymentParams, originalData) {
  const form = document.createElement('form');
  form.method = 'POST';
   form.target = '_self'; // ✅ Added: Ensure same window navigation
  form.action = process.env.REACT_APP_PAYU_ENV === 'production'
    ? 'https://secure.payu.in/_payment'
    : 'https://test.payu.in/_payment';

  console.log('💳 PayU form action (target):', form.action);
  console.log('💳 PayU payment params:', paymentParams);

  // Required PayU fields
  const formFields = {
    key: paymentParams.key,
    txnid: paymentParams.txnid,
    amount: paymentParams.amount,
    productinfo: paymentParams.productinfo,
    firstname: paymentParams.firstname,
    email: paymentParams.email,
    phone: paymentParams.phone || originalData.phone || '',
    surl: paymentParams.surl,
    furl: paymentParams.furl,
    hash: paymentParams.hash,
    udf1: paymentParams.udf1 || originalData.udf1 || '',
    udf2: paymentParams.udf2 || originalData.udf2 || '',
  };

  // Create hidden input fields
  Object.entries(formFields).forEach(([key, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = value || '';
    form.appendChild(input);
  });

  // Add form to body and submit
  document.body.appendChild(form);
  console.log('✅ Submitting payment form to PayU');
  form.submit();

  // Note: Form submission will navigate the current WebView to PayU
  // After payment, PayU redirects to surl/furl which are backend URLs
  // Backend then redirects to frontend /paymentResult
}


/**
 * Get stored transaction data
 */
function getStoredTransactionData() {
  try {
    const data = localStorage.getItem('payuTransaction');
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Failed to get transaction data:', error);
    return null;
  }
}

/**
 * Clear transaction data
 */
function clearTransactionData() {
  try {
    localStorage.removeItem('payuTransaction');
    console.log('🗑️ Transaction data cleared');
  } catch (error) {
    console.error('Failed to clear transaction data:', error);
  }
}

/**
 * Generate unique transaction ID
 */
export function generateTransactionId(userId, packageId) {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 10000);
  return `TXN${timestamp}_${userId}_${packageId}_${random}`;
}

/**
 * Normalize phone number to 10 digits
 */
export function normalizePhoneNumber(phone) {
  if (!phone) return '';
  
  // Remove all non-digits
  let cleaned = String(phone).replace(/\D/g, '');
  
  // If starts with 91, remove it
  if (cleaned.startsWith('91') && cleaned.length === 12) {
    cleaned = cleaned.substring(2);
  }
  
  // Ensure 10 digits
  if (cleaned.length === 10) {
    return cleaned;
  }
  
  return '';
}

/**
 * Check if running in Capacitor native app
 */
export function isNativeApp() {
  return Capacitor.isNativePlatform();
}

/**
 * Get platform info
 */
export function getPlatformInfo() {
  return {
    platform: Capacitor.getPlatform(),
    isNative: Capacitor.isNativePlatform(),
    isWeb: !Capacitor.isNativePlatform(),
  };
}

export default {
  initiateWebViewPayment,
  generateTransactionId,
  normalizePhoneNumber,
  isNativeApp,
  getPlatformInfo,
  getStoredTransactionData,
  clearTransactionData,
};
